import { ref, readonly, type Ref, type DeepReadonly } from "vue";
import { captureException } from "@sentry/vue";
import { adminV1OrganizationsPaymentsScheduledPaymentsAmendCreate } from "../api";
import type { AmendPaymentRequest } from "@/api/model";
import { ActionEnum } from "@/modules/payments/utils/amendmentAction";
import { useCurrentOrganization } from "@/composables/useCurrentOrganization";
import { addMixpanelEvent, EVENT_NAMES } from "@/shared/utils/analytics";

interface AmendPaymentOptionsBase {
  scheduledPaymentId: string;
  tagSlug?: string | null;
  notes?: string;
  applyToAll?: boolean;
}

interface ChangeAmountOptions extends AmendPaymentOptionsBase {
  action: typeof ActionEnum.change_amount;
  newAmount: number;
}

interface WaiveOptions extends AmendPaymentOptionsBase {
  action: typeof ActionEnum.waive;
}

// Discriminated on `action` so the type system enforces that `newAmount` is
// required for change-amount and unavailable for waive. Drops the previous
// `as number` cast at the request-body builder.
export type AmendPaymentOptions = ChangeAmountOptions | WaiveOptions;

interface UseAmendPaymentReturn {
  isLoading: DeepReadonly<Ref<boolean>>;
  error: DeepReadonly<Ref<string | null>>;
  amendPayment: (options: AmendPaymentOptions) => Promise<boolean>;
}

export function useAmendPayment(): UseAmendPaymentReturn {
  const { currentOrganizationId } = useCurrentOrganization();
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  const _handleError = (err: unknown): void => {
    captureException(err, {
      extra: { message: "Failed to amend scheduled payment" },
    });
    const status = (err as { response?: { status?: number } })?.response
      ?.status;
    if (status === 401 || status === 403) {
      error.value = "You don't have permission to amend this payment";
      return;
    }
    if (status === 404) {
      error.value = "Scheduled payment not found";
      return;
    }
    if (status === 400) {
      error.value = "Invalid amendment data";
      return;
    }
    error.value = "Failed to amend payment";
  };

  const amendPayment = async (
    options: AmendPaymentOptions
  ): Promise<boolean> => {
    if (isLoading.value) return false; // pending guard
    if (!currentOrganizationId.value) {
      error.value = "Organization not found";
      return false;
    }

    isLoading.value = true;
    error.value = null;

    try {
      const body: AmendPaymentRequest =
        options.action === ActionEnum.waive
          ? {
              action: ActionEnum.waive,
              tag_slug: options.tagSlug ?? null,
              notes: options.notes ?? "",
              apply_to_all: options.applyToAll ?? false,
            }
          : {
              action: ActionEnum.change_amount,
              new_amount: options.newAmount,
              tag_slug: options.tagSlug ?? null,
              notes: options.notes ?? "",
              apply_to_all: options.applyToAll ?? false,
            };
      await adminV1OrganizationsPaymentsScheduledPaymentsAmendCreate(
        currentOrganizationId.value,
        options.scheduledPaymentId,
        body
      );

      const eventName =
        options.action === ActionEnum.waive
          ? EVENT_NAMES.PAYMENTS.ScheduledPaymentWaived
          : EVENT_NAMES.PAYMENTS.ScheduledPaymentAmountChanged;
      addMixpanelEvent(eventName, {
        scheduledPaymentId: options.scheduledPaymentId,
        tagSlug: options.tagSlug ?? null,
        newAmount:
          options.action === ActionEnum.waive ? null : options.newAmount,
        hasNotes: Boolean(options.notes?.trim()),
      });

      return true;
    } catch (err: unknown) {
      _handleError(err);
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    isLoading: readonly(isLoading),
    error: readonly(error),
    amendPayment,
  };
}
